import { useState, useEffect, useCallback, useRef, Component } from 'react';
import './App.css';
import { startResize } from './utils/useResize';
import { supabase } from './utils/supabaseClient';
import { createWatchlistSyncer, reconcileWatchlist, sanitizeCoinIds } from './utils/watchlistSync';
import EmailGate from './components/EmailGate';
import LoadingGate from './components/LoadingGate';
import { trackEvent, trackEventRaw } from './utils/track';
import { settleHandoff } from './sessionHandoff';
import Header from './components/Header';
import Watchlist from './components/Watchlist';
import PriceChart from './components/PriceChart';
import SignalsTab from './components/SignalsTab';
import StrategiesTab from './components/StrategiesTab';
import CalculatorTab from './components/CalculatorTab';
import ScannerTab from './components/ScannerTab';
import TradePlanner from './components/TradePlanner';
import DashboardTab from './components/DashboardTab';
import HeatmapTab from './components/HeatmapTab';
import PortfolioTab from './components/PortfolioTab';
import FundingTab from './components/FundingTab';
import ETFTab from './components/ETFTab';
import CalendarTab from './components/CalendarTab';
import DerivativesTab from './components/DerivativesTab';
import AlertBanner from './components/AlertBanner';
import Toast from './components/Toast';
import { readMarketCache } from './utils/marketCache';
import DataCredits from './components/DataCredits';
import SourceDot from './components/SourceDot';
import { CoinGeckoMark } from './components/home/HomeModule';
import PartnersMenu from './components/PartnersMenu';
import WelcomeModal from './components/WelcomeModal';
import TrialEndedModal from './components/TrialEndedModal';
import ProPreview from './components/ProPreview';
import { useTerminalLevel, PRO_TABS, requiredLevel } from './utils/access';
import TagActivationModal, { WAITING } from './components/TagActivationModal';
import AlertsPanel from './components/AlertsPanel';
import { ALERT_DRAFT_EVENT, openAlertDraft, unseenCount } from './utils/alerts';
import RiskDisclaimerModal from './components/RiskDisclaimerModal';
import { fmtPrice, fmtPct } from './utils/format';
import { cachedFetch } from './utils/api';
import { getFetchMeta, readCache } from './utils/fetchWithCache';
import { fetchBtcDerivSnapshot } from './utils/derivData';
import { playAlertBeep } from './utils/audio';
import { openLanding } from './utils/landing';
import { PRO_PRICE_TEXT, PLUSZ_PRICE_TEXT, PLUSZ_PATH } from './utils/pricing';

const COINGECKO = 'https://api.coingecko.com/api/v3';
const MARKETS_URL = (page) => `${COINGECKO}/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=100&page=${page}&sparkline=true&price_change_percentage=1h,24h,7d`;
const GLOBAL_URL = `${COINGECKO}/global`;
const FNG_URL = 'https://api.alternative.me/fng/?limit=2';
const TRENDING_URL = `${COINGECKO}/search/trending`;
const REFRESH_MS = 60000;

/* Shown when CoinGecko answers 429 — or a CORS failure, which is how a 429
   without CORS headers looks from the browser (fetchWithCache: err.overloaded). */
const OVERLOADED_MSG = 'Az árfolyam-adatforrás (CoinGecko) most túlterhelt. Pár perc múlva automatikusan újrapróbáljuk.';
const RANGE_DAYS = { '7D': 7, '14D': 14, '30D': 30, '90D': 90 };
const DEFAULT_WATCHLIST = [
  'bitcoin', 'ethereum', 'solana', 'binancecoin',
  'ripple', 'dogecoin', 'hyperliquid', 'sui',
];

function WelcomeBackBar({ email, onClose }) {
  const [fading, setFading] = useState(false);
  const [gone,   setGone]   = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => setFading(true), 3000);
    const t2 = setTimeout(() => { setGone(true); onClose?.(); }, 3800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [onClose]);

  if (gone) return null;
  const name = (email ?? '').split('@')[0];
  return (
    <div className={`wb-bar${fading ? ' wb-bar--fade' : ''}`}>
      <span>👋 Üdv vissza, <strong>{name}</strong>! A piacok azóta változtak, hogy legutóbb jártál.</span>
      <button className="wb-close" aria-label="Bezárás" onClick={() => {
        setFading(true);
        setTimeout(() => { setGone(true); onClose?.(); }, 700);
      }}>✕</button>
    </div>
  );
}

class TabErrorBoundary extends Component {
  state = { hasError: false };
  static getDerivedStateFromError() { return { hasError: true }; }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, textAlign: 'center', color: '#ef4444' }}>
          <div style={{ fontSize: 28, marginBottom: 12 }}>⚠️</div>
          <div style={{ marginBottom: 16, color: 'var(--text2)' }}>Ez a szekció hibát tapasztalt.</div>
          <button
            onClick={() => this.setState({ hasError: false })}
            style={{ padding: '8px 20px', background: 'var(--surface2)', border: '1px solid var(--border2)', borderRadius: 5, color: 'var(--text)', cursor: 'pointer', fontFamily: 'var(--font)', fontSize: 13 }}
          >
            Újrapróbálom
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function loadLS(key, fallback) {
  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }
}

function wlKey() {
  const email = localStorage.getItem('terminal_email') || '';
  return email ? `local_watchlist_${email}` : 'local_watchlist';
}

function loadWatchlist() {
  try {
    const v = JSON.parse(localStorage.getItem(wlKey()));
    if (Array.isArray(v) && v.length > 0) return v;
    const old = JSON.parse(localStorage.getItem('watchlist_ids'));
    if (Array.isArray(old) && old.length > 0) {
      localStorage.setItem(wlKey(), JSON.stringify(old));
      return old;
    }
  } catch {}
  return null;
}

function useInterval(fn, ms) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    const id = setInterval(() => ref.current(), ms);
    return () => clearInterval(id);
  }, [ms]);
}

export default function App() {
  // null = still checking, false = no session, object = authenticated user
  const [user, setUser] = useState(null);
  const userRef = useRef(null);
  userRef.current = user;
  const [sessionChecked, setSessionChecked] = useState(false);
  const [access, setAccess] = useState(null);
  const [activationModal, setActivationModal] = useState(null); // null | 'terminal_banner' | 'terminal_expired'
  const tl = useTerminalLevel(access);
  const [trialEndedOpen, setTrialEndedOpen] = useState(false);

  const [coins, setCoins] = useState([]);
  const [globalData, setGlobalData] = useState(null);
  const [selectedCoin, setSelectedCoin] = useState(null);
  const selectedCoinRef = useRef(null);
  selectedCoinRef.current = selectedCoin;
  const [historyData, setHistoryData] = useState(null);
  const [range, setRange] = useState('30D');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  // Freshness of each data source, for the "Forrás · frissítve" footers
  const [marketsMeta,  setMarketsMeta]  = useState(null);
  // Várt frissítési idők (a modulfejléc „késleltetett” jelzéséhez): a szerveroldali
  // cache ütemezése, vagy tartalék úton a közvetlen lekérdezésé.
  const [refreshMs, setRefreshMs] = useState({ markets: 60000, global: 300000, trending: 600000 });
  const [globalMeta,   setGlobalMeta]   = useState(null);
  const [, setFgMeta]       = useState(null);
  const [trendingMeta, setTrendingMeta] = useState(null);
  const [scannerMeta,  setScannerMeta]  = useState(null);
  const [stratDeriv,   setStratDeriv]   = useState({ fundingData: null, lsData: null, meta: null });

  // ── Feature state ──────────────────────────────────────────────────────────
  const [theme, setTheme] = useState(() => loadLS('theme', 'dark'));
  // eslint-disable-next-line no-unused-vars
  const [alerts, setAlerts] = useState(() => loadLS('alerts', []));
  const [notifications, setNotifications] = useState([]);
  const [watchlistIds, setWatchlistIds] = useState(
    () => loadWatchlist() ?? DEFAULT_WATCHLIST
  );
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);

  // ── Onboarding & hints ────────────────────────────────────────────────────
  /* First login: the WelcomeModal follows the risk disclaimer. The
     "onboarding_completed" flag lives in localStorage — profiles has no such
     column (no migration defines it), so there is nothing to write in the DB. */
  const [showWelcome, setShowWelcome] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(
    () => !!localStorage.getItem('w3k_disclaimer_accepted')
  );
  const [showWelcomeBack, setShowWelcomeBack] = useState(false);
  const [showAddHint,     setShowAddHint]     = useState(false);
  const [showScannerHint, setShowScannerHint] = useState(false);
  const [chartCollapsed, setChartCollapsed] = useState(
    () => localStorage.getItem('signals_chart_collapsed') !== 'false'
  );
  const [calcChartCollapsed, setCalcChartCollapsed] = useState(
    () => localStorage.getItem('calculator_chart_collapsed') !== 'false'
  );

  // ── Responsive layout ──────────────────────────────────────────────────────
  const tabsBarRef = useRef(null);
  const [tabsAtEnd, setTabsAtEnd] = useState(false);
  const updateTabsFade = useCallback(() => {
    const el = tabsBarRef.current;
    if (!el) return;
    setTabsAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4);
  }, []);
  useEffect(() => {
    updateTabsFade();
    window.addEventListener('resize', updateTabsFade);
    return () => window.removeEventListener('resize', updateTabsFade);
  });
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const v = parseInt(localStorage.getItem('sidebar_width'));
    return isNaN(v) ? 220 : Math.min(400, Math.max(160, v));
  });
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const sidebarSaveTimer = useRef(null);
  useEffect(() => {
    clearTimeout(sidebarSaveTimer.current);
    sidebarSaveTimer.current = setTimeout(() => {
      localStorage.setItem('sidebar_width', String(sidebarWidth));
    }, 400);
  }, [sidebarWidth]);

  // ── Supabase Auth session ──────────────────────────────────────────────────
  useEffect(() => {
    /* A landingről átadott session előbb álljon be (sessionHandoff.js), különben
       a getSession még üresnek látná, és egy pillanatra az EmailGate villanna fel. */
    settleHandoff()
      .then(() => supabase.auth.getSession())
      .then(({ data }) => {
        const sessionUser = data.session?.user ?? null;
        setUser(sessionUser);
        setSessionChecked(true);
        if (sessionUser) {
          if (sessionUser.email) localStorage.setItem('terminal_email', sessionUser.email);
          const isFirst = !localStorage.getItem('onboarding_completed');
          if (!isFirst) setShowWelcomeBack(true);
        }
      })
      /* A getSession() EL TUD BUKNI (NavigatorLockAcquireTimeout). .catch()
         nélkül a setSessionChecked(true) sosem futott le, és a "Betöltés…"
         képernyő örökre megmaradt. Kijelentkezettként engedjük tovább: az
         EmailGate fogadja a felhasználót, ami használható állapot. */
      .catch((e) => {
        console.warn('[auth] getSession sikertelen:', e?.message || e);
        trackEventRaw('auth_init_failed', {
          stage: 'terminal_get_session',
          error: String(e?.name || 'unknown'),
          message: String(e?.message || '').slice(0, 200),
        });
        setUser(null);
        setSessionChecked(true);
      });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const sessionUser = session?.user ?? null;
      setUser(sessionUser);
      setSessionChecked(true);
      if (sessionUser && sessionUser.email) {
        localStorage.setItem('terminal_email', sessionUser.email);
      }
    });
    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshAccess = useCallback(() => {
    if (!user) { setAccess(null); return; }
    supabase.rpc('get_user_access').single()
      .then(({ data }) => { if (data) setAccess(data); });
  }, [user]);

  useEffect(() => { refreshAccess(); }, [refreshAccess]);

  /* ── Riasztások (2b): panel, piszkozat a terminál bármely pontjáról, csengő ── */
  const [alertsOpen, setAlertsOpen] = useState(false);
  const [alertDraft, setAlertDraft] = useState(null);
  const [alertsUnseen, setAlertsUnseen] = useState(0);
  useEffect(() => {
    const onDraft = (e) => { setAlertDraft(e.detail || null); setAlertsOpen(true); };
    window.addEventListener(ALERT_DRAFT_EVENT, onDraft);
    return () => window.removeEventListener(ALERT_DRAFT_EVENT, onDraft);
  }, []);
  // The alert e-mail links to ?riasztasok=1 — open the panel once signed in.
  useEffect(() => {
    if (!user?.id) return;
    const q = new URLSearchParams(window.location.search);
    if (q.get('riasztasok') === '1') {
      setAlertsOpen(true);
      q.delete('riasztasok');
      const rest = q.toString();
      window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash);
    }
  }, [user?.id]);
  // A riasztás-üzenet linkje: ?s=BTC → erre a coinra áll a terminál (kártya, grafikon).
  // A coinlista betöltése után egyszer fut; a paramétert utána kiveszi az URL-ből.
  const linkSymbolRef = useRef(new URLSearchParams(window.location.search).get('s'));
  useEffect(() => {
    const raw = linkSymbolRef.current;
    if (!raw || !coins.length) return;
    linkSymbolRef.current = null;
    const sym = raw.trim().toLowerCase().replace(/usdt$/, '');
    if (/^[a-z0-9]{1,20}$/.test(sym)) {
      const coin = coins.find(c => c.symbol?.toLowerCase() === sym)
        // nincs a CoinGecko-listában: a kártya Bybit-árat mutat, a grafikon a Bybit-szimbólumot
        ?? { id: `bybit-${sym}`, symbol: sym, name: sym.toUpperCase() };
      setSelectedCoin(coin);
      setHistoryData(null);
      setActiveTab('dashboard');
      trackEvent('active_symbol_changed', { source: 'alert_link' }, user);
    }
    const q = new URLSearchParams(window.location.search);
    q.delete('s');
    const rest = q.toString();
    window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash);
  }, [coins, user]);
  // Telegram deep link: ?s=BTC&src=tg → one tg_alert_action event once signed in,
  // then `src` leaves the URL (`s` stays for the effect above).
  const tgLinkRef = useRef((() => {
    const q = new URLSearchParams(window.location.search);
    return q.get('src') === 'tg' ? { symbol: (q.get('s') || '').trim().toUpperCase() || null } : null;
  })());
  useEffect(() => {
    const link = tgLinkRef.current;
    if (!link || !user?.id) return;
    tgLinkRef.current = null;
    trackEvent('tg_alert_action', { action: 'open', symbol: link.symbol }, user);
    const q = new URLSearchParams(window.location.search);
    q.delete('src');
    const rest = q.toString();
    window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash);
  }, [user]);
  const refreshAlertsUnseen = useCallback(() => {
    if (user?.id) unseenCount(user.id).then(setAlertsUnseen, () => {});
  }, [user?.id]);
  const onAlertsSeen = useCallback(() => setAlertsUnseen(0), []);
  const onAlertDraftDone = useCallback(() => setAlertDraft(null), []);
  useEffect(() => {
    refreshAlertsUnseen();
    const id = setInterval(refreshAlertsUnseen, 60e3);
    return () => clearInterval(id);
  }, [refreshAlertsUnseen]);

  /* Is there a TAG request waiting for us? Then the header shows
     "TAG: feldolgozás alatt" instead of a new activation offer. Re-checked
     when the activation modal closes (a UID may just have been sent). */
  const [tagPending, setTagPending] = useState(false);
  const isFreeTier = access?.tier === 'free';
  useEffect(() => {
    if (!user?.id || !isFreeTier || activationModal) return;
    let cancelled = false;
    supabase.from('affiliate_requests')
      .select('status').eq('user_id', user.id)
      .order('created_at', { ascending: false }).limit(1)
      .then(({ data }) => { if (!cancelled) setTagPending(WAITING.has(data?.[0]?.status)); },
            () => {});
    return () => { cancelled = true; };
  }, [user?.id, isFreeTier, activationModal]);

  // Record last login timestamp once per confirmed session (fire-and-forget)
  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from('profiles')
      .update({ last_login_at: new Date().toISOString() })
      .eq('id', user.id)
      .then(() => {}, () => {});
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSignOut() {
    await supabase.auth.signOut();
    localStorage.removeItem('terminal_access_granted');
    localStorage.removeItem('terminal_email');
    setUser(null);
    setAccess(null);
    setShowWelcomeBack(false);
  }

  // ── Scanner state ──────────────────────────────────────────────────────────
  const [scannerData,    setScannerData]    = useState(null);
  const [fearGreedData,  setFearGreedData]  = useState(null);
  const [trendingData,   setTrendingData]   = useState(null);
  const [scannerLoading, setScannerLoading] = useState(false);
  const [scannerError, setScannerError] = useState(null);
  const [scannerUpdated, setScannerUpdated] = useState(null);
  const scannerAutoFetched  = useRef(false);
  const addHintTimerRef    = useRef(null);
  const page2TimerRef      = useRef(null);
  const scanPage2TimerRef  = useRef(null);

  // Apply theme
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', JSON.stringify(theme));
  }, [theme]);

  // ── Alert checking ─────────────────────────────────────────────────────────
  const checkAlerts = useCallback((freshCoins) => {
    setAlerts(prev => {
      const updated = prev.map(alert => {
        if (alert.triggered) return alert;
        const coin = freshCoins.find(c => c.id === alert.coinId);
        if (!coin) return alert;
        const hit =
          (alert.direction === 'above' && coin.current_price >= alert.price) ||
          (alert.direction === 'below' && coin.current_price <= alert.price);
        return hit ? { ...alert, triggered: true } : alert;
      });
      const fired = updated.filter((a, i) => a.triggered && !prev[i]?.triggered);
      if (fired.length > 0) {
        playAlertBeep();
        setNotifications(prev => [
          ...prev,
          ...fired.map(f => ({ ...f, notifId: `${f.id}-${Date.now()}` })),
        ]);
        localStorage.setItem('alerts', JSON.stringify(updated));
        return updated;
      }
      return prev;
    });
  }, []);

  // ── Data fetching ──────────────────────────────────────────────────────────
  const fetchMarketsPage2 = useCallback(async () => {
    try {
      const data = await cachedFetch(MARKETS_URL(2), 120000);
      setCoins(prev => {
        const ids = new Set(prev.map(c => c.id));
        return [...prev, ...data.filter(c => !ids.has(c.id))];
      });
    } catch (_) {}
  }, []);

  const fetchMarkets = useCallback(async () => {
    // 1) szerveroldali cache (3a) — a CoinGecko-t nem a böngésző hívja
    const cached = await readMarketCache('markets');
    if (cached) {
      setRefreshMs(r => ({ ...r, markets: cached.intervalMs }));
      setMarketsMeta({ at: cached.at, stale: false, via: 'cache', error: cached.error });
      setLastUpdated(new Date(cached.at));
      setError(null); setLoading(false);
      if (cached.changed) {
        const data = cached.data;
        setCoins(data);
        setSelectedCoin(prev => prev ? (data.find(c => c.id === prev.id) ?? prev) : (data[0] ?? null));
        checkAlerts(data);
      }
      return;
    }
    // 2) tartalék: közvetlen CoinGecko (mint 3a előtt)
    setRefreshMs(r => ({ ...r, markets: REFRESH_MS }));
    try {
      const url = MARKETS_URL(1);
      const data = await cachedFetch(url, 60000);
      setCoins(data);
      const meta = getFetchMeta(url);
      setMarketsMeta(meta);
      setLastUpdated(meta?.at ? new Date(meta.at) : new Date());
      setError(null);
      setLoading(false);
      setSelectedCoin(prev => prev ? (data.find(c => c.id === prev.id) ?? prev) : (data[0] ?? null));
      checkAlerts(data);
      clearTimeout(page2TimerRef.current);
      page2TimerRef.current = setTimeout(fetchMarketsPage2, 500);
    } catch (e) {
      if (e.status === 429 || e.overloaded) setError(OVERLOADED_MSG);
      else setError(`Az árfolyamadatok betöltése nem sikerült (${e.message}).`);
      setLoading(false);
    }
  }, [checkAlerts, fetchMarketsPage2]);

  const fetchGlobal = useCallback(async () => {
    const cached = await readMarketCache('global');
    if (cached) {
      setRefreshMs(r => ({ ...r, global: cached.intervalMs }));
      if (cached.changed) setGlobalData(cached.data);
      setGlobalMeta({ at: cached.at, stale: false, via: 'cache', error: cached.error });
      return;
    }
    setRefreshMs(r => ({ ...r, global: 5 * 60000 }));
    try {
      const url = GLOBAL_URL;
      const data = await cachedFetch(url, 5 * 60000);
      setGlobalData(data);
      setGlobalMeta(getFetchMeta(url));
    } catch (_) {}
  }, []);

  const fetchHistory = useCallback(async (coinId, days) => {
    setHistoryData(null);
    try {
      const data = await cachedFetch(
        `${COINGECKO}/coins/${coinId}/market_chart?vs_currency=usd&days=${days}`,
        5 * 60000
      );
      setHistoryData(data);
    } catch (_) {}
  }, []);

  const fetchFearGreed = useCallback(async () => {
    try {
      const url = FNG_URL;
      const data = await cachedFetch(url, 60 * 60000);
      setFearGreedData(data.data);
      setFgMeta(getFetchMeta(url));
    } catch (_) {}
  }, []);

  const fetchTrending = useCallback(async () => {
    const cached = await readMarketCache('trending');
    if (cached) {
      setRefreshMs(r => ({ ...r, trending: cached.intervalMs }));
      if (cached.changed) setTrendingData(cached.data);
      setTrendingMeta({ at: cached.at, stale: false, via: 'cache', error: cached.error });
      return;
    }
    setRefreshMs(r => ({ ...r, trending: 10 * 60000 }));
    try {
      const url = TRENDING_URL;
      const data = await cachedFetch(url, 10 * 60000);
      setTrendingData(data);
      setTrendingMeta(getFetchMeta(url));
    } catch (_) {}
  }, []);

  const fetchScannerData = useCallback(async () => {
    setScannerLoading(true);
    setScannerError(null);
    try {
      const url1 = `${COINGECKO}/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=100&page=1&price_change_percentage=24h,7d`;
      const page1 = await cachedFetch(url1, 30000);
      setScannerData(page1);
      const meta1 = getFetchMeta(url1);
      setScannerMeta(meta1);
      setScannerUpdated(meta1?.at ? new Date(meta1.at) : new Date());
      clearTimeout(scanPage2TimerRef.current);
      scanPage2TimerRef.current = setTimeout(async () => {
        try {
          const page2 = await cachedFetch(
            `${COINGECKO}/coins/markets?vs_currency=usd&order=market_cap_desc&per_page=100&page=2&price_change_percentage=24h,7d`,
            60000
          );
          setScannerData(prev => {
            if (!prev) return page2;
            const ids = new Set(prev.map(c => c.id));
            return [...prev, ...page2.filter(c => !ids.has(c.id))];
          });
        } catch (_) {}
      }, 500);
    } catch (e) {
      if (e.status === 429 || e.overloaded) setScannerError(OVERLOADED_MSG);
      else setScannerError(`A szkenner adatai nem töltődtek be (${e.message}).`);
    } finally {
      setScannerLoading(false);
    }
  }, []);

  /* Start from the last cached answers (even expired ones, marked stale) so a
     returning user sees the terminal immediately; fresh data replaces them
     as it arrives. */
  const hydrateFromCache = useCallback(() => {
    const ageMeta = (c, ttl) => ({ at: c.timestamp, stale: Date.now() - c.timestamp > ttl });
    const m1 = readCache(MARKETS_URL(1));
    if (Array.isArray(m1?.data) && m1.data.length) {
      const m2 = readCache(MARKETS_URL(2));
      const ids = new Set(m1.data.map(c => c.id));
      const all = Array.isArray(m2?.data) ? [...m1.data, ...m2.data.filter(c => !ids.has(c.id))] : m1.data;
      setCoins(all);
      setSelectedCoin(prev => prev ?? all[0] ?? null);
      setMarketsMeta(ageMeta(m1, 60000));
      setLastUpdated(new Date(m1.timestamp));
      setLoading(false);
    }
    const g = readCache(GLOBAL_URL);
    if (g?.data) { setGlobalData(g.data); setGlobalMeta(ageMeta(g, 5 * 60000)); }
    const f = readCache(FNG_URL);
    if (f?.data?.data) { setFearGreedData(f.data.data); setFgMeta(ageMeta(f, 60 * 60000)); }
    const t = readCache(TRENDING_URL);
    if (t?.data) { setTrendingData(t.data); setTrendingMeta(ageMeta(t, 10 * 60000)); }
  }, []);

  /* NO MARKET DATA WITHOUT A SESSION. The login screen used to fire ~5
     CoinGecko calls in the background (and every 60 s), spending the shared
     per-IP rate limit on visitors who had not even signed in. */
  const signedIn = !!user;
  const signedInRef = useRef(signedIn);
  signedInRef.current = signedIn;

  useEffect(() => {
    if (!signedIn) return;
    hydrateFromCache();
    fetchMarkets(); fetchGlobal(); fetchFearGreed(); fetchTrending();
  }, [signedIn, hydrateFromCache, fetchMarkets, fetchGlobal, fetchFearGreed, fetchTrending]);
  useInterval(() => { if (signedInRef.current) fetchMarkets(); },   REFRESH_MS);
  useInterval(() => { if (signedInRef.current) fetchGlobal(); },    REFRESH_MS * 5);
  useInterval(() => { if (signedInRef.current) fetchFearGreed(); }, REFRESH_MS * 60); // once per hour
  useInterval(() => { if (signedInRef.current) fetchTrending(); },  REFRESH_MS * 10); // every 10 min

  // The price chart is only visible on the RSI-áttekintés and Kalkulátor tabs —
  // no market_chart call while the user is elsewhere.
  const chartVisible = activeTab === 'signals' || activeTab === 'calculator';
  useEffect(() => {
    if (selectedCoin && chartVisible) fetchHistory(selectedCoin.id, RANGE_DAYS[range]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCoin?.id, range, fetchHistory, chartVisible]);

  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible' && signedInRef.current) { fetchMarkets(); fetchGlobal(); }
    }
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [fetchMarkets, fetchGlobal]);

  useEffect(() => () => {
    clearTimeout(addHintTimerRef.current);
    clearTimeout(page2TimerRef.current);
    clearTimeout(scanPage2TimerRef.current);
  }, []);

  // Auto-fetch scanner/heatmap data the first time either tab is opened
  useEffect(() => {
    if ((activeTab === 'scanner' || activeTab === 'heatmap') && !scannerAutoFetched.current) {
      scannerAutoFetched.current = true;
      fetchScannerData();
    }
  }, [activeTab, fetchScannerData]);

  // Funding + long/short for the Stratégiák tab (fetched while it is open)
  useEffect(() => {
    // FREE preview: the funding / squeeze cards are blurred → no requests.
    if (activeTab !== 'strategies' || !tl.isPro) return;
    let alive = true;
    const load = () => fetchBtcDerivSnapshot()
      .then(d => { if (alive) setStratDeriv(d); })
      .catch(() => {});
    load();
    const id = setInterval(load, 5 * 60000);
    return () => { alive = false; clearInterval(id); };
  }, [activeTab, tl.isPro]);

  /* The first visit after the trial ended shows a one-time, dismissible
     notice (per account on this device). Never for PRO, and not for an
     account that never started a trial (trial_state 'nincs', Plusz 1. kör);
     an old DB without trial_state keeps the previous rule. */
  const trialEndedKey = user?.email ? `trial_ended_modal_seen_${user.email}` : 'trial_ended_modal_seen';
  useEffect(() => {
    if (!user || !access || access.tier === 'pro' || access.trial_active || !disclaimerAccepted) return;
    if (access.trial_state ? access.trial_state !== 'felhasznalt' : (!tl.isFree || tl.isPaying)) return;
    try { if (!localStorage.getItem(trialEndedKey)) setTrialEndedOpen(true); } catch (_) {}
  }, [user, access, tl.isFree, tl.isPaying, disclaimerAccepted, trialEndedKey]);
  const closeTrialEnded = useCallback(() => {
    try { localStorage.setItem(trialEndedKey, new Date().toISOString()); } catch (_) {}
    setTrialEndedOpen(false);
  }, [trialEndedKey]);

  // Show the tour once: signed in, terminal unlocked, disclaimer accepted.
  useEffect(() => {
    if (!user || !access?.has_terminal || !disclaimerAccepted) return;
    if (!localStorage.getItem('onboarding_completed')) setShowWelcome(true);
  }, [user, access?.has_terminal, disclaimerAccepted]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleDismissNotification = useCallback((notifId) => {
    setNotifications(prev => prev.filter(n => n.notifId !== notifId));
  }, []);

  function handleWelcomeDone() {
    setShowWelcome(false);
    if (!localStorage.getItem('hints_shown')) {
      localStorage.setItem('hints_shown', 'true');
      setShowAddHint(true);
      setShowScannerHint(true);
      addHintTimerRef.current = setTimeout(() => setShowAddHint(false), 5000);
    }
  }

  const handleToggleTheme = useCallback(() => {
    document.documentElement.classList.add('theme-transition');
    setTimeout(() => document.documentElement.classList.remove('theme-transition'), 300);
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  }, []);

  const handleTogglePin = useCallback((coinId, coinName) => {
    setWatchlistIds(prev => {
      if (prev.includes(coinId)) return prev;
      if (prev.length >= 20) {
        setToast({ message: 'A figyelőlista megtelt (max. 20 coin)', id: Date.now() });
        if (toastTimer.current) clearTimeout(toastTimer.current);
        toastTimer.current = setTimeout(() => setToast(null), 3000);
        return prev;
      }
      const next = [...prev, coinId];
      localStorage.setItem(wlKey(), JSON.stringify(next));
      setToast({ message: `${coinName} hozzáadva a figyelőlistához`, id: Date.now() });
      if (toastTimer.current) clearTimeout(toastTimer.current);
      toastTimer.current = setTimeout(() => setToast(null), 3000);
      return next;
    });
  }, []);

  const handleRemoveFromWatchlist = useCallback((coinId) => {
    setWatchlistIds(prev => {
      const next = prev.filter(id => id !== coinId);
      localStorage.setItem(wlKey(), JSON.stringify(next));
      return next;
    });
  }, []);

  /* ── Watchlist server copy (user_watchlists) for the Telegram digest ──────
     localStorage stays authoritative; the server sync is silent best-effort.
     On sign-in: local list present → keep it and push it; local empty →
     restore from the server row (second device). Changes are pushed only
     after that reconciliation, debounced ~1.5 s. */
  const wlSyncerRef = useRef(null);
  if (!wlSyncerRef.current) wlSyncerRef.current = createWatchlistSyncer({ client: supabase });
  const wlReconciledFor = useRef(null);
  const wlLastPushed = useRef(null);

  useEffect(() => {
    const syncer = wlSyncerRef.current;
    syncer.cancel();
    wlReconciledFor.current = null;
    wlLastPushed.current = null;
    const uid = user?.id;
    if (!uid) return undefined;
    let cancelled = false;
    let localIds = null;
    try { localIds = loadWatchlist(); } catch {}
    // 2e/B is switched on per account while it is being tested (bot_2e_ui: everyone once
    // enabled, until then admins only); otherwise the watchlist stays device-local.
    supabase.rpc('bot_2e_ui')
      .then(({ data: on }) => (on === true && !cancelled
        ? reconcileWatchlist({ client: supabase, userId: uid, localIds })
        : { source: 'off' }))
      .then((res) => {
        if (res.source === 'off') return;
        if (cancelled) return;
        if (res.source === 'server') {
          try { localStorage.setItem(wlKey(), JSON.stringify(res.ids)); } catch {}
          wlLastPushed.current = JSON.stringify(sanitizeCoinIds(res.ids));
          setWatchlistIds(res.ids);
        } else if (res.source === 'local') {
          setWatchlistIds(prev => (JSON.stringify(prev) === JSON.stringify(res.ids) ? prev : res.ids));
          wlLastPushed.current = JSON.stringify(sanitizeCoinIds(res.ids));
          syncer.schedule(uid, res.ids);
        }
        wlReconciledFor.current = uid;
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const uid = user?.id;
    if (!uid || wlReconciledFor.current !== uid) return;
    const key = JSON.stringify(sanitizeCoinIds(watchlistIds));
    if (key === wlLastPushed.current) return;
    wlLastPushed.current = key;
    wlSyncerRef.current.schedule(uid, watchlistIds);
  }, [watchlistIds, user?.id]);

  // Do not lose a pending change when the tab is closed within the debounce window.
  useEffect(() => {
    const syncer = wlSyncerRef.current;
    const onHide = () => { syncer.flush(); };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);

  // Egy közös kiválasztott coin; mérés: active_symbol_changed {source}
  const handleSelectCoin = useCallback((coin, source = 'other') => {
    if (!coin) return;
    if (selectedCoinRef.current?.id !== coin.id) trackEvent('active_symbol_changed', { source }, userRef.current);
    setSelectedCoin(coin);
    setHistoryData(null);
  }, []);

  const switchTab = useCallback((tab) => {
    setActiveTab(tab);
    if (isMobile) setSidebarOpen(false);
  }, [isMobile]);

  const handleToggleChart = useCallback(() => {
    setChartCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('signals_chart_collapsed', String(next));
      return next;
    });
  }, []);

  const handleToggleCalcChart = useCallback(() => {
    setCalcChartCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('calculator_chart_collapsed', String(next));
      return next;
    });
  }, []);

  // ── Derived ────────────────────────────────────────────────────────────────
  const isPinned   = selectedCoin ? watchlistIds.includes(selectedCoin.id) : false;
  const priceChange = selectedCoin?.price_change_percentage_24h;
  const isPos      = priceChange >= 0;
  const isDark     = theme === 'dark';
  const isScanner   = activeTab === 'scanner';
  const isPlanner   = activeTab === 'planner';
  const isDashboard = activeTab === 'dashboard';
  const isHeatmap   = activeTab === 'heatmap';
  const isPortfolio = activeTab === 'portfolio';
  const isFunding   = activeTab === 'funding';
  const isETF       = activeTab === 'etf';
  const isCalendar    = activeTab === 'calendar';
  const isDerivatives = activeTab === 'derivatives';

  // ── Reverse-trial gating (decisions live in utils/access.js) ──────────────
  const proChip = (tab) => (tl.level && PRO_TABS.has(tab) && !tl.canOpen(tab)
    ? (requiredLevel(tab) === 'plusz'
        ? <span className="tab-pro-chip tab-plusz-chip" aria-label="Plusz-funkció">PLUSZ</span>
        : <span className="tab-pro-chip" aria-label="PRO-funkció">PRO</span>)
    : null);
  const gate = (tab, node) => {
    if (!PRO_TABS.has(tab) || tl.canOpen(tab)) return node;
    if (!tl.level) return <div className="tab-empty">Betöltés…</div>;
    return <ProPreview tab={tab} user={user} access={access} onTrialStarted={refreshAccess}>{node}</ProPreview>;
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  // Wait for Supabase to resolve the session before rendering the gate
  if (!sessionChecked) {
    return <LoadingGate page="/terminal" stage="terminal_session" />;
  }

  /* A BELÉPÉSHEZ NINCS SZÜKSÉG ÁRFOLYAMOKRA.

     Az EmailGate korábban a lenti fő render belsejében ült, vagyis a piaci
     adatok kapui MÖGÖTT. Ha a CoinGecko nem válaszolt és nem volt használható
     cache, a látogató a "Failed to load" képernyőn ragadt — és nem tudott
     belépni vagy regisztrálni, mert a belépő felület sosem renderelődött.
     Üres localStorage-dzsal (tehát pont az ÚJ látogatónál) ez a teljes
     regisztrációs utat elzárta egy harmadik fél kimaradásán.

     A gate a kapuk ELÉ került. A piaci adatok közben a háttérben töltődnek:
     az adatlekérő effektek a komponens tetején élnek, és a korai return nem
     akadályozza őket — mire a felhasználó belép, jellemzően már megvannak.

     Az EmailGate teljes képernyős, átlátszatlan overlay (.eg-overlay:
     position:fixed, inset:0, tömör háttér), ezért önállóan visszaadva
     ugyanazt mutatja, mint eddig — csak nem rendereljük fölöslegesen a
     mögötte lévő, amúgy sem látható alkalmazást. */
  if (!user) {
    return <EmailGate />;
  }

  if (loading) {
    return <LoadingGate page="/terminal" stage="market_data" message="Piaci adatok betöltése…" />;
  }

  if (error && coins.length === 0) {
    return (
      <div className="error-screen">
        <div className="error-icon">⚠</div>
        <div className="error-title">Nem sikerült betölteni a piaci adatokat</div>
        <div className="error-msg">{error}</div>
        <div className="error-actions">
          <button onClick={() => { setLoading(true); fetchMarkets(); }} className="retry-btn retry-btn--primary">Újrapróbálom</button>
          <button onClick={handleSignOut} className="retry-btn">Kilépés</button>
        </div>
        <div className="error-contact">
          Ha nem javul, írj nekünk: <a href="mailto:hello@web3kurzus.hu">hello@web3kurzus.hu</a>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      {showWelcome && <WelcomeModal onDone={handleWelcomeDone} />}

      <AlertBanner notifications={notifications} onDismiss={handleDismissNotification} />

      <Header
        globalData={globalData}
        theme={theme}
        onToggleTheme={handleToggleTheme}
        onMenuToggle={() => setSidebarOpen(o => !o)}
        onSignOut={handleSignOut}
        userEmail={user?.email}
        access={access}
        user={user}
        onTagClick={tl.isFree && access?.tier === 'free' ? () => setActivationModal('header') : undefined}
        tagPending={tagPending}
        onAlertsClick={user ? () => { setAlertDraft(null); setAlertsOpen(true); } : undefined}
        alertsBadge={alertsUnseen}
        onTrialStarted={refreshAccess}
      />


      {showWelcomeBack && (
        <WelcomeBackBar
          email={user?.email ?? localStorage.getItem('terminal_email') ?? ''}
          onClose={() => setShowWelcomeBack(false)}
        />
      )}

      <div className="main-layout">
        <Watchlist
          coins={coins}
          selectedId={selectedCoin?.id}
          onSelect={c => handleSelectCoin(c, 'watchlist')}
          watchlistIds={watchlistIds}
          onAddToWatchlist={handleTogglePin}
          onRemoveFromWatchlist={handleRemoveFromWatchlist}
          style={isMobile ? undefined : { width: sidebarWidth }}
          isMobile={isMobile}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          showAddHint={showAddHint}
          onAddHintDismiss={() => setShowAddHint(false)}
          meta={marketsMeta}
          expectedMs={refreshMs.markets}
          user={user}
          onTagClick={tl.isFree && access?.tier === 'free'
            ? () => { setSidebarOpen(false); setActivationModal('menu'); }
            : undefined}
          tagPending={tagPending}
        />

        {/* Desktop sidebar drag handle */}
        {!isMobile && (
          <div
            className="sidebar-drag-handle"
            onMouseDown={e => startResize({
              e,
              current: sidebarWidth,
              min: 160,
              max: 400,
              onResize: setSidebarWidth,
            })}
          />
        )}

        {/* Mobile overlay */}
        {isMobile && sidebarOpen && (
          <div className="mobile-overlay" onClick={() => setSidebarOpen(false)} />
        )}

        <main className="main-panel">
          {/* Coin header + chart: hidden in full-canvas tabs */}
          {selectedCoin && !isDashboard && !isScanner && !isPlanner && !isHeatmap && !isPortfolio && !isFunding && !isETF && !isCalendar && !isDerivatives && activeTab !== 'strategies' && (
            <>
              <div className="coin-header">
                <img src={selectedCoin.image} alt={selectedCoin.name} className="coin-header-icon" />
                <div className="coin-header-info">
                  <h1>
                    {selectedCoin.name}
                    <span className="coin-header-symbol"> {selectedCoin.symbol.toUpperCase()}</span>
                  </h1>
                  <div className="coin-header-meta">Rang: #{selectedCoin.market_cap_rank}</div>
                </div>

                <button
                  className={`watchlist-pin-btn ${isPinned ? 'is-pinned' : ''}`}
                  onClick={() => handleTogglePin(selectedCoin.id, selectedCoin.name)}
                  disabled={isPinned}
                >
                  {isPinned ? '✓ Figyelőlistán' : '+ Figyelőlistához adás'}
                </button>

                <button
                  type="button"
                  className="coin-alert-btn"
                  onClick={() => openAlertDraft({ kind: 'price', coin: selectedCoin, source: 'coin_header' })}
                >
                  🔔 Riasztás
                </button>

                <div className="coin-header-price">
                  <div className="price-main">{fmtPrice(selectedCoin.current_price)}</div>
                  <div className={`price-change ${isPos ? 'positive' : 'negative'}`}>
                    {isPos ? '▲' : '▼'} {fmtPct(Math.abs(priceChange))} (24h)
                  </div>
                </div>
                <div className="last-updated">
                  <SourceDot source="CoinGecko" meta={marketsMeta ?? (lastUpdated ? { at: +lastUpdated } : null)} expectedMs={refreshMs.markets} />
                  <CoinGeckoMark />
                </div>
                {(activeTab === 'signals' || activeTab === 'calculator') && (
                  <button
                    className="chart-toggle-btn"
                    onClick={activeTab === 'signals' ? handleToggleChart : handleToggleCalcChart}
                  >
                    {(activeTab === 'signals' ? chartCollapsed : calcChartCollapsed)
                      ? '▼ Grafikon mutatása' : '▲ Grafikon elrejtése'}
                  </button>
                )}
              </div>

              <div
                className="chart-collapse-wrap"
                style={
                  (chartCollapsed && activeTab === 'signals') ||
                  (calcChartCollapsed && activeTab === 'calculator')
                    ? { maxHeight: 0 }
                    : { maxHeight: '420px' }
                }
              >
                <PriceChart
                  historyData={historyData}
                  range={range}
                  onRangeChange={setRange}
                  isDark={isDark}
                />
              </div>
            </>
          )}

          {/* Tabs always visible. On narrow screens the bar scrolls sideways;
              the fading right edge shows there are more tabs until the end. */}
          <div className={`tabs-bar-wrap${tabsAtEnd ? ' tabs-bar-wrap--end' : ''}`}>
          <div className="tabs-bar" ref={tabsBarRef} onScroll={updateTabsFade}>
            <button className={`tab-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
              onClick={() => switchTab('dashboard')}>Terminál</button>
            <button className={`tab-btn ${activeTab === 'signals' ? 'active' : ''}`}
              onClick={() => switchTab('signals')}>RSI-áttekintés{proChip('signals')}</button>
            <button className={`tab-btn ${activeTab === 'strategies' ? 'active' : ''}`}
              onClick={() => switchTab('strategies')}>Stratégiák{proChip('strategies')}</button>
            <button className={`tab-btn ${activeTab === 'scanner' ? 'active' : ''}`}
              onClick={() => { switchTab('scanner'); setShowScannerHint(false); }}>
              Szkenner{proChip('scanner')}{showScannerHint && <span className="tab-hint-dot" />}
            </button>
            <button className={`tab-btn ${activeTab === 'derivatives' ? 'active' : ''}`}
              onClick={() => switchTab('derivatives')}>Derivatívák</button>
            <button className={`tab-btn ${activeTab === 'heatmap' ? 'active' : ''}`}
              onClick={() => switchTab('heatmap')}>Hőtérkép</button>
            <button className={`tab-btn ${activeTab === 'calculator' ? 'active' : ''}`}
              onClick={() => switchTab('calculator')}>Kalkulátor</button>
            <button className={`tab-btn ${activeTab === 'planner' ? 'active' : ''}`}
              onClick={() => switchTab('planner')}>Trade Tervező</button>
            <button className={`tab-btn ${activeTab === 'portfolio' ? 'active' : ''}`}
              onClick={() => switchTab('portfolio')}>Portfólió</button>
            <button className={`tab-btn ${activeTab === 'funding' ? 'active' : ''}`}
              onClick={() => switchTab('funding')}>Funding{proChip('funding')}</button>
            <button className={`tab-btn ${activeTab === 'etf' ? 'active' : ''}`}
              onClick={() => switchTab('etf')}>ETF{proChip('etf')}</button>
            <button className={`tab-btn ${activeTab === 'calendar' ? 'active' : ''}`}
              onClick={() => switchTab('calendar')}>Naptár</button>
          </div>
          </div>

          {access?.trial_active && access?.tier !== 'pro' && access.trial_days_left <= 7 && (
            <div style={{
              background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.2)',
              borderRadius: '8px', padding: '10px 16px', margin: '8px 0',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: '10px', flexWrap: 'wrap',
              fontSize: '13px', color: '#fbbf24',
            }}>
              <span>
                ⏰ {access.trial_days_left > 0
                  ? `${access.trial_days_left} nap múlva lejár a PRO-próbád.`
                  : 'Ma lejár a PRO-próbád.'} Az ingyenes funkciók megmaradnak.{' '}
                {/* Plusz 3. kör: FREE és TAG felé a Plusz a fő ajánlat; Plusz-előfizetőnek a PRO. */}
                {access?.tier === 'plusz'
                  ? <>A Stratégiák, az ETF, a Put/Call és az 50 riasztás PRO-val folytatható: {PRO_PRICE_TEXT}.</>
                  : <>A Szkenner, az RSI-áttekintés és a Funding Plusszal folytatható: {PLUSZ_PRICE_TEXT}. A Stratégiák és az ETF PRO-val.</>}
              </span>
              {access?.tier === 'plusz' ? (
                <button
                  onClick={() => openLanding('/pro', {
                    user,
                    event: 'pro_cta_clicked',
                    eventProps: { source: 'banner', placement: 'trial_7d', tier: access?.tier || null, days_left: access.trial_days_left },
                  })}
                  style={{ color: '#0a0f1e', background: '#A97BFF', fontWeight: 700, border: 'none', borderRadius: 100, padding: '6px 14px', cursor: 'pointer', fontFamily: 'var(--font)', fontSize: '13px', whiteSpace: 'nowrap' }}>
                  PRO részletek →
                </button>
              ) : (
                <button
                  onClick={() => openLanding(PLUSZ_PATH, {
                    user,
                    event: 'plusz_cta_click',
                    eventProps: { hely: 'terminal_proba_7nap' },
                  })}
                  style={{ color: '#0a0f1e', background: '#00D2C8', fontWeight: 700, border: 'none', borderRadius: 100, padding: '6px 14px', cursor: 'pointer', fontFamily: 'var(--font)', fontSize: '13px', whiteSpace: 'nowrap' }}>
                  Plusszal folytatom →
                </button>
              )}
            </div>
          )}

          <div className="tab-content" style={(isDashboard || isHeatmap) && !isMobile ? { overflow:'hidden', display:'flex', flexDirection:'column' } : {}}>
            <TabErrorBoundary key={activeTab}>
              {activeTab === 'dashboard' && (
                <DashboardTab
                  coins={coins}
                  fearGreedData={fearGreedData}
                  trendingData={trendingData}
                  marketsMeta={marketsMeta}
                  trendingMeta={trendingMeta}
                  onSelectCoin={handleSelectCoin}
                  selectedCoin={selectedCoin}
                  user={user}
                  refreshMs={refreshMs}
                  marketsError={!!error}
                  onRetry={{ markets: fetchMarkets, trending: fetchTrending }}
                />
              )}
              {activeTab === 'signals' && (
                selectedCoin
                  ? gate('signals', <SignalsTab
                      coins={coins}
                      selectedCoinId={selectedCoin?.id}
                      globalData={globalData}
                      fearGreedData={fearGreedData}
                      trendingData={trendingData}
                      marketsMeta={marketsMeta}
                      globalMeta={globalMeta}
                      trendingMeta={trendingMeta}
                      previewLimit={tl.isFree ? 5 : undefined}
                      onSelectCoin={coin => { handleSelectCoin(coin, 'signals'); }}
                    />)
                  : <div className="tab-empty">Válassz egy coint a figyelőlistából.</div>
              )}
              {activeTab === 'strategies' && (
                gate('strategies', <StrategiesTab
                  coins={coins}
                  fundingData={stratDeriv.fundingData}
                  lsData={stratDeriv.lsData}
                  previewLimit={tl.isPro ? undefined : 5}
                  marketsMeta={marketsMeta}
                  derivMeta={stratDeriv.meta}
                />)
              )}
              {activeTab === 'calculator' && (
                selectedCoin
                  ? <CalculatorTab selectedCoin={selectedCoin} coins={coins} onSelectCoin={c => handleSelectCoin(c, 'calculator')} />
                  : <div className="tab-empty">Válassz egy coint a figyelőlistából.</div>
              )}
              {activeTab === 'scanner' && (
                gate('scanner', <ScannerTab
                  data={scannerData}
                  loading={scannerLoading}
                  error={scannerError}
                  lastUpdated={scannerUpdated}
                  onRefresh={fetchScannerData}
                  meta={scannerMeta}
                  coins={coins}
                  coinsMeta={marketsMeta}
                  preview={tl.isFree}
                />)
              )}
              {activeTab === 'planner' && (
                <TradePlanner coins={coins} selectedCoin={selectedCoin} onSelectCoin={c => handleSelectCoin(c, 'planner')} />
              )}
              {activeTab === 'heatmap' && (
                <HeatmapTab
                  isFree={tl.isFree}
                  user={user}
                  access={access}
                  data={scannerData}
                  loading={scannerLoading}
                  error={scannerError}
                  lastUpdated={scannerUpdated}
                  meta={scannerMeta}
                  onRefresh={fetchScannerData}
                  onSelectCoin={coin => { handleSelectCoin(coin, 'scanner'); switchTab('dashboard'); }}
                  watchlistCoins={coins}
                />
              )}
              {activeTab === 'portfolio' && (
                <PortfolioTab coins={coins} scannerData={scannerData} />
              )}
              {activeTab === 'funding' && (
                gate('funding', <FundingTab />)
              )}
              {activeTab === 'etf' && (
                gate('etf', <ETFTab btcPrice={coins.find(c => c.id === 'bitcoin')?.current_price ?? null} />)
              )}
              {activeTab === 'calendar' && (
                <CalendarTab />
              )}
              {activeTab === 'derivatives' && (
                <DerivativesTab fearGreedData={fearGreedData} isFree={tl.isFree} putCall={tl.isPro} user={user} access={access} />
              )}
            </TabErrorBoundary>
          </div>
        </main>
      </div>

      <div className="risk-bar">
        <span>⚠️ Az elemzések nem minősülnek befektetési tanácsnak. A kereskedés kockázatos.</span>
        <DataCredits />
        <PartnersMenu />
      </div>

      {toast && <Toast message={toast.message} />}

      {trialEndedOpen && (
        <TrialEndedModal
          access={access}
          user={user}
          onClose={closeTrialEnded}
          showPlusz={access?.tier !== 'plusz'}
          onTagClick={access?.tier === 'free' ? () => { closeTrialEnded(); setActivationModal('expired_modal'); } : undefined}
        />
      )}

      <AlertsPanel
        open={alertsOpen}
        onClose={() => setAlertsOpen(false)}
        user={user}
        isPro={tl.isPro}
        level={tl.level || 'free'}
        draft={alertDraft}
        onDraftDone={onAlertDraftDone}
        defaultCoin={selectedCoin}
        onSeen={onAlertsSeen}
      />

      <TagActivationModal
        isOpen={!!activationModal}
        source={activationModal || 'unknown'}
        onClose={() => setActivationModal(null)}
        onActivated={refreshAccess}
        user={user}
        tier={access?.tier}
      />

      <RiskDisclaimerModal onAccept={() => setDisclaimerAccepted(true)} />
    </div>
  );
}
